/**
 * Platform capability table. Design doc §10.3 and §15.1 (D22, D23 locked).
 *
 * ⚠️ Every value is to be verified against a real test stream in Phase 3.
 */
import type { Fps, Orientation, Platform, Quality } from "./enums";
import { QUALITY_RANK, type Profile } from "./profiles";

export interface PlatformCaps {
  maxQuality: Quality;
  maxFps: Fps;
  orientations: readonly Orientation[];
  /** Default ingest URL. Editable by the user only for `custom_rtmp`. ⚠️ verify before launch. */
  defaultRtmpUrl: string | null;
  /**
   * Longest single broadcast the platform allows, in hours; null = unknown or unlimited.
   * When a disconnect happens at about this duration it is an expected restart (`platform_session_limit`, D22).
   * ⚠️ Facebook's 8 hours is from a secondary source; verify.
   */
  maxSessionHours: number | null;
}

export const PLATFORM_CAPS: Record<Platform, PlatformCaps> = {
  youtube: {
    maxQuality: "4k",
    maxFps: 60,
    orientations: ["landscape", "portrait"], // D23: portrait on YouTube and custom RTMP only in v1
    defaultRtmpUrl: "rtmp://a.rtmp.youtube.com/live2",
    maxSessionHours: null,
  },
  facebook: {
    maxQuality: "hd",
    maxFps: 60,
    orientations: ["landscape"],
    defaultRtmpUrl: "rtmps://live-api-s.facebook.com:443/rtmp/",
    maxSessionHours: 8,
  },
  twitch: {
    maxQuality: "hd",
    maxFps: 60,
    orientations: ["landscape"],
    defaultRtmpUrl: "rtmp://live.twitch.tv/app",
    maxSessionHours: null,
  },
  custom_rtmp: {
    maxQuality: "4k",
    maxFps: 60,
    orientations: ["landscape", "portrait"],
    defaultRtmpUrl: null,
    maxSessionHours: null,
  },
};

export interface PlanLimits {
  maxQuality: Quality;
  maxFps: Fps;
}

export type ProfileViolation = "QUALITY_NOT_ALLOWED" | "FPS_NOT_ALLOWED" | "ORIENTATION_NOT_SUPPORTED";

/**
 * Checks a stream's profile against the plan and the platform (§13.5 step 3).
 * Returns every violation (empty array = allowed).
 */
export function profileViolations(plan: PlanLimits, platform: Platform, profile: Profile): ProfileViolation[] {
  const caps = PLATFORM_CAPS[platform];
  const out: ProfileViolation[] = [];
  if (QUALITY_RANK[profile.quality] > QUALITY_RANK[plan.maxQuality] || QUALITY_RANK[profile.quality] > QUALITY_RANK[caps.maxQuality]) {
    out.push("QUALITY_NOT_ALLOWED");
  }
  if (profile.fps > plan.maxFps || profile.fps > caps.maxFps) out.push("FPS_NOT_ALLOWED");
  if (!caps.orientations.includes(profile.orientation)) out.push("ORIENTATION_NOT_SUPPORTED");
  return out;
}

/** Effective limit for a stream = the minimum of the plan limit and the platform limit. */
export function effectiveLimits(plan: PlanLimits, platform: Platform): PlanLimits {
  const caps = PLATFORM_CAPS[platform];
  return {
    maxQuality: QUALITY_RANK[plan.maxQuality] <= QUALITY_RANK[caps.maxQuality] ? plan.maxQuality : caps.maxQuality,
    maxFps: plan.maxFps <= caps.maxFps ? plan.maxFps : caps.maxFps,
  };
}
